import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Play, LinkIcon, Plus, Trash2, Lock, Music, Eye, GripVertical, ArrowUpDown, ChevronDown, Crown } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Progress } from '@/components/ui/progress';
import type { OnboardingStepProps } from '../onboardingSteps';
import { createShowcase, fetchShowcases, deleteShowcase, reorderShowcases, updateShowcase, backfillShowcaseMetadata, fetchYouTubeDetails, fetchCategories } from '../onboardingApi';
import axiosInstance from '../../../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { usePlan } from '@/context/PlanContext';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Types ───────────────────────────────────────────────────

interface CategoryItem {
  _id: string;
  name: string;
  parentCategory?: { _id: string; name: string } | null;
}

interface ShowcaseItem {
  _id: string;
  title: string;
  youtube: string;
  thumbnail: string;
  category: string;
  isActive?: boolean;
  metadata?: {
    views?: number;
    channelTitle?: string;
    releaseDate?: string;
    duration?: number;
  };
  externalIds?: {
    youtube?: string;
    spotify?: string;
  };
  createdAt?: string;
}

type SortMode = 'custom' | 'views' | 'latest';

// ─── Helpers ─────────────────────────────────────────────────

function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function formatViews(n?: number): string {
  if (!n || n <= 0) return '';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function getEntityId(value: unknown): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  const entity = value as { _id?: unknown; id?: unknown };
  return String(entity._id ?? entity.id ?? '');
}


// ─── Sortable Track Row ──────────────────────────────────────

interface SortableTrackProps {
  item: ShowcaseItem;
  index: number;
  isLocked: boolean;
  categories: CategoryItem[];
  onDelete: (id: string) => void;
  onCategoryChange: (id: string, categoryId: string) => void;
}

function SortableTrack({ item, index, isLocked, categories, onDelete, onCategoryChange }: SortableTrackProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : undefined,
  };

  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    };
    if (catOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [catOpen]);

  const ytId = item.externalIds?.youtube || extractYouTubeVideoId(item.youtube || '');
  const thumbUrl = item.thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '');
  const views = item.metadata?.views;
  const channelTitle = item.metadata?.channelTitle;
  const releaseDate = item.metadata?.releaseDate || item.createdAt;

  const parentCategories = categories.filter(c => !c.parentCategory);
  const currentCat = categories.find(c => c._id === item.category);
  const catDisplayName = currentCat
    ? (currentCat.parentCategory ? `${currentCat.parentCategory.name} › ${currentCat.name}` : currentCat.name)
    : 'Music';

  return (
    <div ref={setNodeRef} style={style} className={`group ${isDragging ? 'shadow-lg rounded-lg' : ''}`}>
      <Card className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 transition-all ${
        isLocked ? 'bg-muted/30' : ''
      } ${isDragging ? 'ring-2 ring-primary/30' : ''}`}>
        {/* Drag handle */}
        {!isLocked ? (
          <button
            className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground shrink-0 p-0.5"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        ) : (
          <div className="shrink-0 p-0.5">
            <GripVertical className="w-4 h-4 text-muted-foreground/30 cursor-not-allowed" />
          </div>
        )}

        {/* Index badge */}
        <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
          isLocked ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
        }`}>
          {index + 1}
        </div>

        {/* Thumbnail */}
        <div className="relative w-16 h-11 sm:w-20 sm:h-14 rounded-md overflow-hidden bg-muted shrink-0">
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={item.title}
              className={`w-full h-full object-cover ${isLocked ? 'blur-[1px] brightness-75' : ''}`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (ytId && !target.src.includes('hqdefault.jpg')) {
                  target.src = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
                } else {
                  target.style.display = 'none';
                  // Show music icon as final fallback
                  target.insertAdjacentHTML('afterend', '<div class="w-full h-full flex items-center justify-center"><svg class="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-5.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/></svg></div>');
                }
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          {!isLocked && ytId && (
            <a
              href={`https://www.youtube.com/watch?v=${ytId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <div className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
              </div>
            </a>
          )}
          {isLocked && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Lock className="w-3.5 h-3.5 text-white/80" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-0.5">
          <p className={`text-sm font-medium truncate ${isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>
            {item.title}
          </p>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
            {channelTitle && (
              <span className="truncate max-w-30">{channelTitle}</span>
            )}
            {views != null && views > 0 && (
              <span className="flex items-center gap-0.5 shrink-0">
                <Eye className="w-3 h-3" />
                {formatViews(views)}
              </span>
            )}
            {releaseDate && (
              <span className="shrink-0">{formatDate(releaseDate)}</span>
            )}
          </div>
        </div>

        {/* Category selector (compact) */}
        {!isLocked && (
          <div className="relative shrink-0" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen(!catOpen)}
              className="flex items-center gap-1 text-sm text-foreground bg-secondary hover:bg-accent rounded-md px-2 py-1 transition-colors max-w-25 cursor-pointer"
            >
              <span className="truncate">{catDisplayName}</span>
              <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${catOpen ? 'rotate-180' : ''}`} />
            </button>
            {catOpen && (
              <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-35 max-h-48 overflow-y-auto">
                {parentCategories.map(cat => (
                  <button
                    key={cat._id}
                    type="button"
                    onClick={() => {
                      onCategoryChange(item._id, cat._id);
                      setCatOpen(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                      item.category === cat._id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upgrade badge OR delete */}
        {isLocked ? (
          <div className="shrink-0 w-8" />
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-destructive shrink-0"
            onClick={() => onDelete(item._id)}
            aria-label={`Delete ${item.title}`}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </Card>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────

const ShowcaseOnboarding: React.FC<OnboardingStepProps> = ({ saveRef, onValidationChange, onPreviewChange, backgroundStatus }) => {
  const { canAdd, openUpgradeModal, getUsage, refreshPlanInfo } = usePlan();
  const showcaseUsage = getUsage('showcases');

  // Showcases from API
  const [showcases, setShowcases] = useState<ShowcaseItem[]>([]);
  const [loadingShowcases, setLoadingShowcases] = useState(true);

  // Progress tracking state
  const [discoveryProgress, setDiscoveryProgress] = useState<{
    stage: 'initializing' | 'scanning_label' | 'scanning_search' | 'scanning_artist' | 'ranking' | 'prefilling' | 'complete' | 'error' | string;
    progress: number;
    currentStep: string;
    totalSteps: number;
    completedSteps: number;
    error?: string;
    startedAt?: string;
    completedAt?: string;
  } | null>(null);
  const progressPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Smooth progress — instead of jumping 20→40→60→80, increment by 1
  // every ~50ms so the bar crawls smoothly toward the polled target.
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const targetProgressRef = useRef(0);

  // Tick displayedProgress toward the target every 50ms
  useEffect(() => {
    const timer = setInterval(() => {
      setDisplayedProgress((prev) => {
        const target = targetProgressRef.current;
        if (prev >= target) return prev;
        return Math.min(prev + 1, target);
      });
    }, 50);
    return () => clearInterval(timer);
  }, []);

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [mediaType, setMediaType] = useState<'youtube' | 'soundcloud' | 'spotify'>('youtube');
  const [urlError, setUrlError] = useState('');
  // YouTube-specific
  const [videoId, setVideoId] = useState<string | null>(null);
  // Shared across all platforms
  const [videoTitle, setVideoTitle] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  // SC / Spotify canonical URLs
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Categories
  const [allCategories, setAllCategories] = useState<CategoryItem[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [addCatOpen, setAddCatOpen] = useState(false);
  const addCatRef = useRef<HTMLDivElement>(null);

  // Sort & ordering — default to 'views' (Most Views). The views order is
  // persisted to the backend on load so Showcase Management shows the exact
  // same sequence.
  const [sortMode, setSortMode] = useState<SortMode>('views');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const parentCategories = allCategories.filter(c => !c.parentCategory);
  const limitReached = showcaseUsage ? !showcaseUsage.canAdd : !canAdd('showcases');

  useEffect(() => {
    if (loadingShowcases) return;
    const previewCategories = allCategories.length > 0
      ? allCategories
      : [{ _id: 'onboarding-preview-music', name: 'Music', parentCategory: null }];
    const validCategoryIds = new Set(previewCategories.map(category => category._id));
    const fallbackCategoryId = validCategoryIds.has(selectedCategoryId)
      ? selectedCategoryId
      : (previewCategories.find(category => !category.parentCategory)?._id ?? previewCategories[0]._id);
    const previewShowcases = showcases.map(item => {
      const categoryId = getEntityId(item.category);
      return {
        ...item,
        category: validCategoryIds.has(categoryId) ? categoryId : fallbackCategoryId,
      };
    });
    const draftItem = showAddForm && selectedCategoryId && (videoId || soundcloudUrl || spotifyUrl)
      ? [{
          _id: 'onboarding-draft-showcase',
          title: videoTitle || 'Untitled Performance',
          youtube: videoId ? urlInput.trim() : '',
          soundcloudUrl,
          spotifyUrl,
          thumbnail: thumbnail || (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : ''),
          category: selectedCategoryId,
          isActive: true,
        }]
      : [];
    onPreviewChange?.({ music: { items: [...draftItem, ...previewShowcases], categories: previewCategories } });
  }, [loadingShowcases, showcases, allCategories, showAddForm, selectedCategoryId, videoId, soundcloudUrl, spotifyUrl, videoTitle, urlInput, thumbnail, onPreviewChange]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Progress polling ──
  const stopProgressPolling = useCallback(() => {
    if (progressPollingRef.current) {
      clearInterval(progressPollingRef.current);
      progressPollingRef.current = null;
    }
  }, []);

  // Start polling when background music discovery is in progress
  useEffect(() => {
    if (backgroundStatus != null && !backgroundStatus.musicReady) {
      // Don't double-start
      if (progressPollingRef.current) return;

      const poll = async () => {
        try {
          const res = await axiosInstance.get('/api/identity/progress');
          if (res.data?.success && res.data.progress) {
            setDiscoveryProgress(res.data.progress);
            // Update the target — the timer ticks toward it smoothly
            targetProgressRef.current = Math.max(0, Math.min(100, res.data.progress.progress ?? 0));

            // Stop polling on terminal stages
            if (
              res.data.progress.stage === 'complete' ||
              res.data.progress.stage === 'error'
            ) {
              stopProgressPolling();
            }
          }
        } catch {
          // Non-fatal — keep polling
        }
      };

      // Poll immediately, then every 2 seconds
      poll();
      progressPollingRef.current = setInterval(poll, 2000);
    } else {
      stopProgressPolling();
    }

    return () => stopProgressPolling();
  }, [backgroundStatus?.musicReady, stopProgressPolling]);

  // ── Load data ──
  // Sort by views (desc) and persist the order to the backend if it changed,
  // so the sequence shown here matches Showcase Management and the public site.
  const applyViewsOrder = async (items: ShowcaseItem[]): Promise<ShowcaseItem[]> => {
    const sorted = [...items].sort((a, b) => (b.metadata?.views ?? 0) - (a.metadata?.views ?? 0));
    const changed = sorted.some((item, i) => item._id !== items[i]._id);
    if (changed) {
      try {
        await reorderShowcases(sorted.map(i => ({ _id: i._id })));
      } catch { /* silent — order will refresh on next load */ }
    }
    return sorted;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [existingShowcases, categories] = await Promise.all([
          fetchShowcases(),
          fetchCategories('showcase'),
        ]);
        let items = Array.isArray(existingShowcases) ? existingShowcases : [];
        setAllCategories(Array.isArray(categories) ? categories as CategoryItem[] : []);

        // Default add-form category to first parent
        const parents = (categories as CategoryItem[]).filter(c => !c.parentCategory);
        if (parents.length) setSelectedCategoryId(parents[0]._id);

        // Auto-backfill metadata if any showcases are missing views/channelTitle
        const needsBackfill = items.some(
          (s: ShowcaseItem) => s.externalIds?.youtube && (s.metadata?.views == null || !s.metadata?.channelTitle)
        );
        if (needsBackfill) {
          try {
            const result = await backfillShowcaseMetadata();
            if (result?.updated > 0) {
              items = await fetchShowcases();
              items = Array.isArray(items) ? items : [];
            }
          } catch { /* silent */ }
        }

        setShowcases(await applyViewsOrder(items));
      } catch { /* silent */ } finally {
        setLoadingShowcases(false);
      }
    };
    load();
  }, []);

  // Re-fetch when background music discovery completes
  useEffect(() => {
    if (!backgroundStatus?.musicReady) return;
    if (showcases.length > 0) return; // already have items, no need to re-fetch

    const refetch = async () => {
      setLoadingShowcases(true);
      try {
        let items = await fetchShowcases();
        items = Array.isArray(items) ? items : [];
        if (items.length > 0) {
          // Backfill metadata for newly discovered songs
          try {
            const result = await backfillShowcaseMetadata();
            if (result?.updated > 0) {
              items = await fetchShowcases();
              items = Array.isArray(items) ? items : [];
            }
          } catch { /* silent */ }
        }
        setShowcases(await applyViewsOrder(items));
      } catch { /* silent */ } finally {
        setLoadingShowcases(false);
      }
    };
    refetch();
  }, [backgroundStatus?.musicReady]);

  // ── Sorted display list ──
  const getSortedShowcases = useCallback((): ShowcaseItem[] => {
    const items = [...showcases];
    if (sortMode === 'views') {
      items.sort((a, b) => (b.metadata?.views ?? 0) - (a.metadata?.views ?? 0));
    } else if (sortMode === 'latest') {
      items.sort((a, b) => {
        const dateA = a.metadata?.releaseDate || a.createdAt || '';
        const dateB = b.metadata?.releaseDate || b.createdAt || '';
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
    }
    // 'custom' uses existing order (from API)
    return items;
  }, [showcases, sortMode]);

  const displayItems = getSortedShowcases();

  const showcaseLimit = showcaseUsage?.limit ?? -1;
  const unlockedItems = showcaseLimit === -1 ? displayItems : displayItems.slice(0, showcaseLimit);
  const lockedItems = showcaseLimit === -1 ? [] : displayItems.slice(showcaseLimit);
  const lockedCount = lockedItems.length;

  // ── Validation ──
  const hasValidMedia = !!videoId || !!soundcloudUrl || !!spotifyUrl;
  useEffect(() => {
    if (showAddForm && hasValidMedia) {
      onValidationChange(!!selectedCategoryId);
    } else {
      onValidationChange(displayItems.length > 0 || backgroundStatus?.musicReady === true);
    }
  }, [onValidationChange, showAddForm, hasValidMedia, selectedCategoryId, displayItems.length, backgroundStatus?.musicReady]);

  // ── Click-outside handlers ──
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortDropdownOpen(false);
      if (addCatRef.current && !addCatRef.current.contains(e.target as Node)) setAddCatOpen(false);
    };
    if (sortDropdownOpen || addCatOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [sortDropdownOpen, addCatOpen]);

  // ── Fetch helpers ──
  const fetchSoundCloudDetails = async (url: string) => {
    const res = await axiosInstance.get(`${BASE_URL}/api/soundcloud/track-details`, { params: { url } });
    return res.data;
  };
  const fetchSpotifyDetails = async (url: string) => {
    const res = await axiosInstance.get(`${BASE_URL}/api/spotify/track-details`, { params: { url } });
    return res.data;
  };

  // ── Smart URL handler ──
  const handleUrlInputChange = async (value: string) => {
    setUrlInput(value);
    setUrlError('');

    if (!value) {
      setVideoId(null); setThumbnail(''); setVideoTitle('');
      setSoundcloudUrl(''); setSpotifyUrl('');
      setMediaType('youtube'); // reset default
      return;
    }

    // ── YouTube ──
    const ytId = extractYouTubeVideoId(value);
    if (ytId) {
      setMediaType('youtube');
      setVideoId(ytId);
      setSoundcloudUrl(''); setSpotifyUrl('');
      setThumbnail(`https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`);
      setVideoTitle('');
      setIsFetching(true);
      try {
        const data = await fetchYouTubeDetails(ytId);
        if (data?.title) setVideoTitle(data.title);
      } catch { /* silent */ } finally { setIsFetching(false); }
      return;
    }

    // ── SoundCloud ──
    if (value.includes('soundcloud.com/')) {
      setMediaType('soundcloud');
      setSoundcloudUrl(value);
      setVideoId(null); setSpotifyUrl('');
      setIsFetching(true);
      try {
        const details = await fetchSoundCloudDetails(value);
        if (details) {
          setVideoTitle(details.title || '');
          setThumbnail(details.thumbnail || '');
        }
      } catch { /* silent */ } finally { setIsFetching(false); }
      return;
    }

    // ── Spotify ──
    if (value.includes('spotify.com/track/')) {
      setMediaType('spotify');
      setSpotifyUrl(value);
      setVideoId(null); setSoundcloudUrl('');
      setIsFetching(true);
      try {
        const details = await fetchSpotifyDetails(value);
        if (details) {
          setVideoTitle(details.title || '');
          setThumbnail(details.thumbnail || '');
        }
      } catch { /* silent */ } finally { setIsFetching(false); }
      return;
    }

    // ── Platform detected but wrong format ──
    if (value.includes('youtube.com') || value.includes('youtu.be')) {
      setUrlError('YouTube link detected — paste a direct video link (e.g. youtube.com/watch?v=... or youtu.be/...).');
      setMediaType('youtube');
    } else if (value.includes('spotify.com')) {
      setUrlError('Spotify link detected — paste a track link (e.g. open.spotify.com/track/...).');
      setMediaType('spotify');
    } else if (value.includes('soundcloud.com')) {
      setUrlError('SoundCloud link detected — paste a direct track link (e.g. soundcloud.com/artist/track-name).');
      setMediaType('soundcloud');
    } else if (value.startsWith('http://') || value.startsWith('https://')) {
      setUrlError('Please paste a valid YouTube, Spotify, or SoundCloud link.');
    }
    // Not a URL yet — user is still typing, show nothing
    setVideoId(null); setSoundcloudUrl(''); setSpotifyUrl('');
    setThumbnail(''); setVideoTitle('');
  };

  // ── Save ref for Continue button ──
  useEffect(() => {
    saveRef.current = async () => {
      if (showAddForm && hasValidMedia && selectedCategoryId) {
        try {
          const payload: Record<string, unknown> = {
            title: videoTitle || 'Untitled Performance',
            thumbnail,
            category: selectedCategoryId,
            isActive: true,
          };
          if (videoId) {
            payload.youtube = urlInput.trim();
            payload.thumbnail = thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
          } else if (soundcloudUrl) {
            payload.soundcloudUrl = soundcloudUrl;
          } else if (spotifyUrl) {
            payload.spotifyUrl = spotifyUrl;
          }
          const created = await createShowcase(payload);
          setShowcases(prev => [created, ...prev]);
          resetForm();
          refreshPlanInfo();
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to add video. Please try again.');
          throw err;
        }
      }
    };
  }, [saveRef, showAddForm, hasValidMedia, videoId, urlInput, soundcloudUrl, spotifyUrl, videoTitle, thumbnail, selectedCategoryId]);

  // ── Handlers ──

  const resetForm = () => {
    setUrlInput('');
    setUrlError('');
    setVideoId(null);
    setSoundcloudUrl('');
    setSpotifyUrl('');
    setVideoTitle('');
    setThumbnail('');
    setError('');
    setShowAddForm(false);
  };

  const handleAddVideo = async () => {
    setError('');
    // Check plan limit before saving
    if (!canAdd('showcases')) {
      openUpgradeModal('showcases');
      return;
    }
    if (!hasValidMedia) { setError('Please paste a valid YouTube, Spotify, or SoundCloud link.'); return; }
    if (!selectedCategoryId) { setError('Please select a category.'); return; }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        title: videoTitle || 'Untitled Performance',
        thumbnail,
        category: selectedCategoryId,
        isActive: true,
      };
      if (videoId) {
        payload.youtube = urlInput.trim();
        payload.thumbnail = thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      } else if (soundcloudUrl) {
        payload.soundcloudUrl = soundcloudUrl;
      } else if (spotifyUrl) {
        payload.spotifyUrl = spotifyUrl;
      }
      const created = await createShowcase(payload);
      setShowcases(prev => [created, ...prev]);
      resetForm();
      refreshPlanInfo();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add video.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVideo = async (id: string) => {
    const prev = [...showcases];
    setShowcases(s => s.filter(v => v._id !== id));
    try {
      await deleteShowcase(id);
      refreshPlanInfo();
    } catch {
      setShowcases(prev);
    }
  };

  const handleCategoryChange = async (showcaseId: string, categoryId: string) => {
    const item = showcases.find(s => s._id === showcaseId);
    if (!item) return;
    // Optimistic update
    setShowcases(prev => prev.map(s => s._id === showcaseId ? { ...s, category: categoryId } : s));
    try {
      await updateShowcase(showcaseId, { category: categoryId, youtube: item.youtube });
    } catch {
      // Revert on failure
      setShowcases(prev => prev.map(s => s._id === showcaseId ? { ...s, category: item.category } : s));
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = displayItems.findIndex(i => i._id === active.id);
    const newIndex = displayItems.findIndex(i => i._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(displayItems, oldIndex, newIndex);
    setShowcases(reordered);
    setSortMode('custom');

    try {
      await reorderShowcases(reordered.map(i => ({ _id: i._id })));
    } catch { /* silent — order will refresh on next load */ }
  };

  const handleAddClick = () => {
    if (!canAdd('showcases')) { openUpgradeModal('showcases'); return; }
    setShowAddForm(true);
  };

  const handleSortChange = async (mode: SortMode) => {
    setSortMode(mode);
    setSortDropdownOpen(false);
    if (mode === 'custom') return;

    // Persist the sorted order so Showcase Management (and the public site)
    // show the exact same sequence the user sees here.
    const items = [...showcases];
    if (mode === 'views') {
      items.sort((a, b) => (b.metadata?.views ?? 0) - (a.metadata?.views ?? 0));
    } else {
      items.sort((a, b) => {
        const dateA = a.metadata?.releaseDate || a.createdAt || '';
        const dateB = b.metadata?.releaseDate || b.createdAt || '';
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
    }
    setShowcases(items);
    try {
      await reorderShowcases(items.map(i => ({ _id: i._id })));
    } catch { /* silent — order will refresh on next load */ }
  };

  const sortLabels: Record<SortMode, string> = {
    custom: 'Custom Order',
    views: 'Most Views',
    latest: 'Latest First',
  };

  // ── Render ──

  const musicDiscovering = backgroundStatus != null && !backgroundStatus.musicReady;

  // If music discovery starts while the add form is open, close it.
  // The system is about to auto-populate the showcase, so a manual add
  // would be redundant and could race with the background write.
  // We only fire on the rising edge (false → true) to avoid stomping
  // on the user's input if the flag ever flickers.
  const prevMusicDiscoveringRef = useRef(musicDiscovering);
  useEffect(() => {
    const wasDiscovering = prevMusicDiscoveringRef.current;
    prevMusicDiscoveringRef.current = musicDiscovering;
    if (musicDiscovering && !wasDiscovering && showAddForm) {
      resetForm();
    }
    // Reset smooth progress counter on new discovery
    if (musicDiscovering && !wasDiscovering) {
      setDisplayedProgress(0);
      targetProgressRef.current = 0;
    }
  }, [musicDiscovering]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingShowcases) {
    return (
      <div className="space-y-4 onb-fade-in">
        {musicDiscovering || backgroundStatus?.musicReady ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Spinner size="sm" />
                <span className="text-sm font-medium text-foreground truncate">
                  {discoveryProgress?.currentStep || 'Discovering your music from YouTube…'}
                </span>
              </div>
              <span className="text-sm font-mono font-semibold text-primary tabular-nums shrink-0">
                {displayedProgress}%
              </span>
            </div>
            <Progress value={displayedProgress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              Almost done — hang tight while we load your tracks…
            </p>
          </>
        ) : (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 onb-fade-in">
      {/* Header row: stats + sort + limit */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-md text-muted-foreground">
          <span>
            <span className="font-medium text-foreground">{unlockedItems.length}</span> active
            {lockedCount > 0 && (
              <>
                {' · '}
                <span className="font-medium text-muted-foreground">{lockedCount} locked</span>
              </>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          {unlockedItems.length > 1 && (
            <div className="relative" ref={sortRef}>
              <button
                type="button"
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                className="flex items-center gap-1.5 text-sm text-foreground bg-secondary hover:bg-accent rounded-md px-2.5 py-1.5 transition-colors cursor-pointer"
              >
                <ArrowUpDown className="w-3 h-3" />
                {sortLabels[sortMode]}
                <ChevronDown className={`w-3 h-3 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {sortDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-35">
                  {(Object.keys(sortLabels) as SortMode[]).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleSortChange(mode)}
                      className={`w-full text-left px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                        sortMode === mode ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {sortLabels[mode]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Plan limit badge */}
          {/* {showcaseUsage && showcaseUsage.limit !== -1 && (
            <span className="text-[11px] font-medium text-muted-foreground bg-secondary px-2 py-1 rounded-full">
              {unlockedItems.length} / {showcaseUsage.limit}
            </span>
          )} */}
        </div>
      </div>

      {/* Music discovery in progress — show progress bar when background sync is running */}
      {musicDiscovering && (
        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Spinner size="sm" />
              <span className="text-sm font-medium text-foreground truncate">
                {discoveryProgress?.currentStep || 'Discovering your music from YouTube…'}
              </span>
            </div>
            <span className="text-sm font-mono font-semibold text-primary tabular-nums shrink-0">
              {displayedProgress}%
            </span>
          </div>

          <Progress
            value={displayedProgress}
            className="h-2"
          />

          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            {discoveryProgress?.startedAt && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  Started {new Date(discoveryProgress.startedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </span>
              </span>
            )}
            {discoveryProgress?.totalSteps != null && (
              <span className="font-mono tabular-nums">
                Step {Math.min(discoveryProgress.completedSteps + (discoveryProgress.stage === 'complete' ? 0 : 1), discoveryProgress.totalSteps)} of {discoveryProgress.totalSteps}
              </span>
            )}
          </div>

          {discoveryProgress?.stage === 'error' && (
            <p className="text-xs text-destructive">
              {discoveryProgress.error || 'Music discovery failed. You can still add tracks manually below.'}
            </p>
          )}
        </div>
      )}

      {backgroundStatus?.musicReady && showcases.length === 0 && !showAddForm && (
        <p className="text-sm text-muted-foreground">
          No matching tracks were found. You can continue to the next step or add a video manually.
        </p>
      )}

      {/* Add form */}
      {showAddForm ? (
        <Card className="p-4 space-y-4 border-dashed">
          <div className="space-y-1.5">
            <Label>YouTube, Spotify, or SoundCloud link</Label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                value={urlInput}
                onChange={e => handleUrlInputChange(e.target.value)}
                placeholder="Paste a YouTube, Spotify, or SoundCloud link..."
                className="pl-10"
              />
            </div>
            {/* Detected badge — show immediately when platform is detected */}
            {(mediaType && (hasValidMedia || urlError)) && !isFetching && (
              <div className={`flex items-center gap-1.5 text-sm font-medium ${
                mediaType === 'youtube' ? 'text-[#FF0000]'
                : mediaType === 'soundcloud' ? 'text-[#FF5500]'
                : 'text-[#1DB954]'
              }`}>
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="currentColor">
                  {mediaType === 'youtube' && <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>}
                  {mediaType === 'soundcloud' && <path d="M1.175 12.225C.528 12.225 0 12.772 0 13.437c0 .666.528 1.213 1.175 1.213.647 0 1.175-.547 1.175-1.213 0-.665-.528-1.212-1.175-1.212zm3.608 0c-.647 0-1.175.547-1.175 1.212 0 .666.528 1.213 1.175 1.213.647 0 1.175-.547 1.175-1.213 0-.665-.528-1.212-1.175-1.212zm3.608 0c-.647 0-1.175.547-1.175 1.212 0 .666.528 1.213 1.175 1.213.647 0 1.175-.547 1.175-1.213 0-.665-.528-1.212-1.175-1.212zm3.355-8.516c-2.094 0-3.891 1.312-4.637 3.18a3.604 3.604 0 0 0-.694-.068c-1.99 0-3.608 1.634-3.608 3.647 0 2.013 1.618 3.647 3.608 3.647h10.16C19.165 14.115 20 13.272 20 12.215c0-1.057-.834-1.9-1.825-1.9-.19 0-.375.03-.55.086C17.444 8.198 15.614 6 13.302 6c-.525 0-1.03.1-1.506.284a5.056 5.056 0 0 0-1.05-.575z"/>}
                  {mediaType === 'spotify' && <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>}
                </svg>
                <span>Detected: {mediaType === 'youtube' ? 'YouTube' : mediaType === 'soundcloud' ? 'SoundCloud' : 'Spotify'}</span>
              </div>
            )}
            {isFetching && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Spinner size="xs" />
                <span>Fetching details...</span>
              </div>
            )}
            {urlError && <p className="text-sm text-destructive">{urlError}</p>}
          </div>

          {hasValidMedia && (
            <div className="space-y-1.5">
              <Label>Title</Label>
              {isFetching ? (
                <div className="h-9 bg-muted rounded-lg animate-pulse" />
              ) : (
                <Input
                  value={videoTitle}
                  onChange={e => setVideoTitle(e.target.value)}
                  placeholder="Enter a title"
                />
              )}
            </div>
          )}

          {/* Category selector */}
          <div className="space-y-1.5">
            <Label>Category <span className="text-red-500">*</span></Label>
            {parentCategories.length > 0 ? (
              <div className="relative" ref={addCatRef}>
                <button
                  type="button"
                  onClick={() => setAddCatOpen(!addCatOpen)}
                  className="px-3 py-2 rounded-md bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2 w-full justify-between cursor-pointer"
                >
                  {selectedCategoryId
                    ? parentCategories.find(p => p._id === selectedCategoryId)?.name || 'Select Category'
                    : 'Select Category'}
                  <ChevronDown className={`w-4 h-4 transition-transform ${addCatOpen ? 'rotate-180' : ''}`} />
                </button>
                {addCatOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-full max-h-48 overflow-y-auto">
                    {parentCategories.map(cat => (
                      <button
                        key={cat._id}
                        type="button"
                        onClick={() => { setSelectedCategoryId(cat._id); setAddCatOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer ${
                          selectedCategoryId === cat._id
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No categories available.</p>
            )}
            {error === 'Please select a category.' && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>

          {/* Preview */}
          {thumbnail && hasValidMedia && (
            <div className="bg-secondary rounded-lg border border-border overflow-hidden max-w-full sm:max-w-xs">
              <div className="relative aspect-video bg-muted">
                <img
                  src={thumbnail}
                  alt="Thumbnail"
                  className="w-full h-full object-cover"
                  onError={e => {
                    if (videoId) (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                    else (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                {videoId && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                      <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-2">
                {isFetching ? (
                  <div className="h-4 bg-muted rounded animate-pulse w-36" />
                ) : (
                  <p className="text-xs font-medium text-foreground truncate">{videoTitle || 'Untitled Performance'}</p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-1">
            {error && error !== 'Please select a category.' && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleAddVideo} loading={saving} loadingText="Adding...">Add Video</Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        </Card>
      ) : musicDiscovering ? null : (
        // Keep the add affordance visible at the current plan limit so the
        // artist has a direct path to the appropriate upgrade.
        <Button
          type="button"
          variant="outline"
          onClick={handleAddClick}
          className="w-full rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 py-3.5 font-medium text-primary hover:text-primary"
        >
          {limitReached ? (
            <><Lock className="w-4 h-4" /> Upgrade to add more videos</>
          ) : (
            <><Plus className="w-4 h-4" /> Add Video</>
          )}
        </Button>
      )}

      {/* Track list with drag-and-drop (unlocked items draggable, locked visible but non-interactive) */}
      {displayItems.length > 0 && (
        <div className="space-y-1.5">
          {/* Category info */}
          <p className="text-sm text-muted-foreground px-1">
            Drag to reorder (switches to custom order)
            {' · '}All tracks default to <span className="font-medium text-foreground">Music</span> — override per track as needed
          </p>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={displayItems.map(i => i._id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1.5">
                {/* Unlocked tracks — fully interactive */}
                {unlockedItems.map((item, index) => (
                  <SortableTrack
                    key={item._id}
                    item={item}
                    index={index}
                    isLocked={false}
                    categories={allCategories}
                    onDelete={handleDeleteVideo}
                    onCategoryChange={handleCategoryChange}
                  />
                ))}

                {/* Floating upgrade button — sticky at top of locked section */}
                {lockedCount > 0 && (
                  <div className="sticky top-20 z-20 mb-2">
                    <div className="flex justify-center">
                      <Button
                        variant="default"
                        size="lg"
                        className="shadow-xl bg-primary/95 backdrop-blur-sm"
                        onClick={() => openUpgradeModal('showcases')}
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade to unlock {lockedCount} track{lockedCount > 1 ? 's' : ''}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Locked tracks — visible but non-interactive */}
                {lockedItems.map((item, index) => (
                  <SortableTrack
                    key={item._id}
                    item={item}
                    index={unlockedItems.length + index}
                    isLocked={true}
                    categories={allCategories}
                    onDelete={() => {}}
                    onCategoryChange={() => {}}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Empty state — no tracks at all */}
      {unlockedItems.length === 0 && lockedCount === 0 && !showAddForm && (
        <div className="text-center py-10 px-4 border-2 border-dashed border-border rounded-xl">
          <Music className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-1">No tracks added yet</p>
          <p className="text-xs text-muted-foreground/70 mb-4">Add your best performances from YouTube, Spotify, or SoundCloud</p>
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/50 mb-4">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M1.175 12.225C.528 12.225 0 12.772 0 13.437c0 .666.528 1.213 1.175 1.213.647 0 1.175-.547 1.175-1.213 0-.665-.528-1.212-1.175-1.212zm3.608 0c-.647 0-1.175.547-1.175 1.212 0 .666.528 1.213 1.175 1.213.647 0 1.175-.547 1.175-1.213 0-.665-.528-1.212-1.175-1.212zm3.608 0c-.647 0-1.175.547-1.175 1.212 0 .666.528 1.213 1.175 1.213.647 0 1.175-.547 1.175-1.213 0-.665-.528-1.212-1.175-1.212zm3.355-8.516c-2.094 0-3.891 1.312-4.637 3.18a3.604 3.604 0 0 0-.694-.068c-1.99 0-3.608 1.634-3.608 3.647 0 2.013 1.618 3.647 3.608 3.647h10.16C19.165 14.115 20 13.272 20 12.215c0-1.057-.834-1.9-1.825-1.9-.19 0-.375.03-.55.086C17.444 8.198 15.614 6 13.302 6c-.525 0-1.03.1-1.506.284a5.056 5.056 0 0 0-1.05-.575z"/></svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowcaseOnboarding;
